import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';

export type DisclosureVariant = 'chip' | 'bare';

export interface DisclosureButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Drives `aria-expanded`; callers render their own caret/label from the same flag. */
  open: boolean;
  /**
   * `chip` — a thin pill (1px border, rounded-999, muted) for a standalone
   * "show details" affordance. `bare` — a chrome-less row (no border/padding)
   * for a list item whose whole line toggles a preview. Default `bare`.
   */
  variant?: DisclosureVariant;
  children: ReactNode;
}

const CHIP_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 8px',
  background: 'transparent',
  border: '1px solid var(--border)',
  borderRadius: 999,
  color: 'var(--text-muted)',
  fontSize: 11,
  fontFamily: 'inherit',
};

const BARE_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 0,
  background: 'transparent',
  border: 'none',
  color: 'var(--text-secondary)',
  fontSize: 11,
  textAlign: 'left',
  fontFamily: 'inherit',
};

/**
 * Expand/collapse trigger for in-place disclosures (provenance "how this was
 * computed", citation/result-row preview rows). Owns the button shell —
 * `aria-expanded`, the disabled cursor, and the two shared looks — while the
 * caller supplies the inner content (carets, labels, badges) so the same shell
 * hosts a one-line chip or a multi-badge citation row. Font sizing stays with
 * the caller via `style`.
 */
export const DisclosureButton = forwardRef<HTMLButtonElement, DisclosureButtonProps>(
  function DisclosureButton({ open, variant = 'bare', disabled, style, children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        aria-expanded={open}
        disabled={disabled}
        style={{
          ...(variant === 'chip' ? CHIP_STYLE : BARE_STYLE),
          cursor: disabled ? 'default' : 'pointer',
          ...style,
        }}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
